import type {
  DataSource,
  ImportSeriesRequest,
  ImportSeriesResponse,
  LookupResponse,
  RefreshResponse,
  SourceSeries,
  SourceSettingsUpdate,
  SourceStatus,
} from '@panelvault/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, toQuery } from '../../lib/api';

interface SearchNote {
  source: DataSource;
  status: 'hit' | 'miss' | 'skipped' | 'error' | 'cached';
  message: string | null;
}

export function useSources() {
  return useQuery({
    queryKey: ['sources'],
    queryFn: () => api.get<SourceStatus[]>('/sources'),
  });
}

export function useUpdateSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ source, update }: { source: DataSource; update: SourceSettingsUpdate }) =>
      api.patch<SourceStatus>(`/sources/${source}`, update),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sources'] }),
  });
}

export function useVerifySource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (source: DataSource) => api.post<SourceStatus>(`/sources/${source}/verify`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sources'] }),
  });
}

export function useClearSourceCache() {
  return useMutation({
    mutationFn: () => api.delete<{ removed: number }>('/sources/cache'),
  });
}

export function useLookup() {
  return useMutation({
    mutationFn: (input: { code: string; addon?: string }) =>
      api.post<LookupResponse>('/lookup', input),
  });
}

export function useSeriesSearch(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['sources', 'search', trimmed],
    queryFn: () =>
      api.get<{ results: SourceSeries[]; searched: SearchNote[] }>(
        `/sources/search${toQuery({ q: trimmed })}`,
      ),
    enabled: trimmed.length >= 2,
    staleTime: 5 * 60_000,
  });
}

export function useImportSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ImportSeriesRequest) =>
      api.post<ImportSeriesResponse>('/sources/import-series', request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['series'] });
      void queryClient.invalidateQueries({ queryKey: ['collection'] });
      void queryClient.invalidateQueries({ queryKey: ['publishers'] });
      void queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useRefreshIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, includeCover }: { issueId: string; includeCover: boolean }) =>
      api.post<RefreshResponse>(`/issues/${issueId}/refresh`, { includeCover }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['series'] });
      void queryClient.invalidateQueries({ queryKey: ['collection'] });
    },
  });
}

export type { SearchNote };
